import api from './api'
import {
  Cliente,
  ClienteRequest,
  ClienteUpdateRequest,
  ContatoRequest,
  Segmento,
  VolumeMensal,
  VolumeMensalRequest,
} from '@/types/cliente'
import { PageResponse } from '@/types/comum'
import { normalizarCnpj } from '@/utils/cnpj'

const BASE_URL = '/api/v1/clientes'

// Backend de clientes devolve respostas cruas (sem envolver em ApiResponse).
// O interceptor de `api` já entrega o body — basta retornar `response`.
export const clienteService = {
  listar: async (page: number = 0, size: number = 10): Promise<PageResponse<Cliente>> => {
    const response = await api.get<PageResponse<Cliente>>(
      `${BASE_URL}?page=${page}&size=${size}`
    )
    return response as unknown as PageResponse<Cliente>
  },

  // Catálogo de segmentos. Vem do backend em vez de constante no front para os
  // 24 rótulos não divergirem do enum quando a lista mudar.
  listarSegmentos: async (): Promise<Segmento[]> => {
    const response = await api.get<Segmento[]>(`${BASE_URL}/segmentos`)
    return response as unknown as Segmento[]
  },

  // Volume operado do cliente ativo (2º PDF, Etapa 18). Lançamento é OPERADOR+;
  // a leitura segue o escopo padrão de cliente.
  historicoVolumes: async (clienteId: string): Promise<VolumeMensal[]> => {
    const response = await api.get<VolumeMensal[]>(`${BASE_URL}/${clienteId}/volumes`)
    return response as unknown as VolumeMensal[]
  },

  registrarVolume: async (
    clienteId: string,
    request: VolumeMensalRequest
  ): Promise<VolumeMensal> => {
    const response = await api.post<VolumeMensal>(`${BASE_URL}/${clienteId}/volumes`, request)
    return response as unknown as VolumeMensal
  },

  removerVolume: async (clienteId: string, volumeId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${clienteId}/volumes/${volumeId}`)
  },

  buscar: async (id: string): Promise<Cliente> => {
    const response = await api.get<Cliente>(`${BASE_URL}/${id}`)
    return response as unknown as Cliente
  },

  // Busca direta por CNPJ (normalizado: sem máscara, maiúsculas — pode ser alfanumérico). Retorna null em 404
  // (cliente inexistente no escopo do usuário) em vez de lançar — o chamador
  // trata "não cadastrado" como fluxo normal.
  buscarPorCnpj: async (cnpj: string): Promise<Cliente | null> => {
    const normalizado = normalizarCnpj(cnpj)
    try {
      const response = await api.get<Cliente>(`${BASE_URL}/buscar-por-cnpj?cnpj=${normalizado}`)
      return response as unknown as Cliente
    } catch (e) {
      if ((e as { status?: number })?.status === 404) return null
      throw e
    }
  },

  criar: async (request: ClienteRequest): Promise<Cliente> => {
    const response = await api.post<Cliente>(BASE_URL, request)
    return response as unknown as Cliente
  },

  atualizar: async (id: string, request: ClienteUpdateRequest): Promise<Cliente> => {
    const response = await api.put<Cliente>(`${BASE_URL}/${id}`, request)
    return response as unknown as Cliente
  },

  deletar: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`)
  },

  adicionarContato: async (clienteId: string, request: ContatoRequest): Promise<Cliente> => {
    const response = await api.post<Cliente>(`${BASE_URL}/${clienteId}/contatos`, request)
    return response as unknown as Cliente
  },

  removerContato: async (clienteId: string, contatoId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${clienteId}/contatos/${contatoId}`)
  },

  filtrar: async (
    page: number = 0,
    size: number = 10,
    filtros: Record<string, string>
  ): Promise<PageResponse<Cliente>> => {
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('size', String(size))

    Object.entries(filtros).forEach(([key, value]) => {
      if (value) params.append(key, value)
    })

    const response = await api.get<PageResponse<Cliente>>(`${BASE_URL}?${params.toString()}`)
    return response as unknown as PageResponse<Cliente>
  },
}
